import type { FC, PropsWithChildren } from "react"
import { createContext, useContext, useEffect, useMemo } from "react"
import * as Sentry from "@sentry/remix"
import {
  addShopifyCartBreadcrumb,
  setShopifyCartContext
} from "~/helpers/sentry"
import type {
  AttributeInput,
  CartInput,
  CartLineInput,
  MailingAddressInput,
  WithShopifyCartFragment,
  WithShopifyCartLineFragment
} from "~/shopify"
import {
  ShopifyCartCreateDocument,
  ShopifyCartDocument,
  ShopifyCartLinesAddDocument,
  ShopifyCartDiscountCodesUpdateDocument,
  ShopifyCartAttributesUpdateDocument,
  ShopifyBuyerIdentityUpdateDocument,
  CountryCode
} from "~/shopify"
import {
  CHECKOUT_KEY,
  toShopifyStorefrontId,
  getCustomAttributes,
  getRedirectAttributes
} from "~/helpers/shopify"
import usePersistedState from "~/hooks/usePersistedState"
import { findLast } from "lodash-es"
import type { PublicConfigValues } from "~/providers/ConfigProvider"
import { useConfigContext } from "~/providers/ConfigProvider"
import {
  useQuery,
  type ApolloClient,
  type NormalizedCacheObject
} from "@apollo/client/index.js"
import { useShopifyClient } from "~/providers/ShopifyClientProvider"
import {
  useSproutSocialAnalytics,
  useTikTokAnalytics
} from "./AnalyticsProvider"

interface ShopifyCartContextValue {
  loading: boolean
  checkout: WithShopifyCartFragment | undefined
  setCheckoutId: (checkoutId: string) => void
  ensureCheckoutIsCreated: () => Promise<string>
  addLineItems: (
    cartId: string,
    lineItemInputs: CartLineInput[]
  ) => Promise<WithShopifyCartLineFragment[]>
  ensureCheckoutIsCreatedAndAddLineItem: (
    variantCheckoutId: string,
    quantity: number,
    options?: {
      customAttributes?: AttributeInput[]
    },
    sellingPlanId?: string
  ) => Promise<WithShopifyCartLineFragment>
  updateBuyerIdentity: (cartId: string, email?: string) => void
}

const ShopifyCartContext = createContext<ShopifyCartContextValue | undefined>(
  undefined
)

export const ShopifyCartProvider: FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const config = useConfigContext()
  const [checkoutId, setCheckoutId] = usePersistedState(CHECKOUT_KEY)
  const { ttclid } = useTikTokAnalytics()
  const { sproutTxid } = useSproutSocialAnalytics()

  const client = useShopifyClient()
  const { data, loading } = useQuery(ShopifyCartDocument, {
    fetchPolicy: "cache-and-network",
    variables: {
      cartId: checkoutId ?? ""
    },
    skip: !checkoutId,
    client
  })

  const checkout = data?.cart?.__typename === "Cart" ? data.cart : undefined

  const value: ShopifyCartContextValue = useMemo(
    () => ({
      loading,
      checkout,
      setCheckoutId,
      async ensureCheckoutIsCreated() {
        addShopifyCartBreadcrumb("Ensuring checkout is created", { checkoutId })

        try {
          let currentOrNewCheckoutId
          if (checkoutId && (await verifyShopifyCheckout(client, checkoutId))) {
            currentOrNewCheckoutId = checkoutId
            addShopifyCartBreadcrumb("Existing checkout verified", {
              checkoutId
            })
          } else {
            const newCheckout = await createShopifyCheckout(
              client,
              config,
              undefined,
              ttclid,
              sproutTxid
            )
            currentOrNewCheckoutId = newCheckout.id
            setCheckoutId(newCheckout.id)
            setShopifyCartContext(newCheckout.id)
            addShopifyCartBreadcrumb("New checkout created", {
              cartId: newCheckout.id
            })
          }
          return currentOrNewCheckoutId
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              context: "shopify_cart",
              operation: "ensure_checkout_created"
            },
            extra: {
              checkoutId
            }
          })
          throw error
        }
      },
      async addLineItems(checkoutId, lineItemInputs) {
        addShopifyCartBreadcrumb("Adding line items", {
          checkoutId,
          itemCount: lineItemInputs.length
        })

        try {
          const shopifyLineItemInputs = lineItemInputs.map(li => ({
            merchandiseId: li.merchandiseId,
            quantity: li.quantity,
            attributes: li.attributes,
            sellingPlanId: li.sellingPlanId
          }))

          const lineItems = await addLineItems(
            client,
            checkoutId,
            shopifyLineItemInputs
          )

          addShopifyCartBreadcrumb("Line items added successfully", {
            checkoutId,
            itemCount: lineItems.length
          })
          return lineItems
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              context: "shopify_cart",
              operation: "context_add_line_items"
            },
            extra: {
              checkoutId,
              lineItemInputs
            }
          })
          throw error
        }
      },
      async ensureCheckoutIsCreatedAndAddLineItem(
        variantCheckoutId,
        quantity,
        { customAttributes = [] } = {},
        sellingPlanId?: string | undefined
      ) {
        addShopifyCartBreadcrumb("Ensuring checkout and adding line item", {
          variantCheckoutId,
          quantity,
          hasCustomAttributes: customAttributes.length > 0,
          hasSellingPlan: !!sellingPlanId
        })

        try {
          const variantId = toShopifyStorefrontId(
            "ProductVariant",
            variantCheckoutId
          )

          let currentOrNewCheckoutId
          if (checkoutId && (await verifyShopifyCheckout(client, checkoutId))) {
            currentOrNewCheckoutId = checkoutId
          } else {
            const newCheckout = await createShopifyCheckout(
              client,
              config,
              undefined,
              ttclid,
              sproutTxid
            )
            currentOrNewCheckoutId = newCheckout.id
            setCheckoutId(newCheckout.id)
            setShopifyCartContext(newCheckout.id)
          }

          const lineItem = await addLineItem(
            client,
            currentOrNewCheckoutId,
            variantId,
            quantity,
            customAttributes,
            sellingPlanId
          )

          addShopifyCartBreadcrumb("Line item added to checkout", {
            cartId: currentOrNewCheckoutId,
            lineItemId: lineItem.id
          })

          return lineItem
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              context: "shopify_cart",
              operation: "ensure_checkout_and_add_line_item"
            },
            extra: {
              variantCheckoutId,
              quantity,
              customAttributes,
              sellingPlanId,
              checkoutId
            }
          })
          throw error
        }
      },
      async updateBuyerIdentity(cartId: string, email: string | undefined) {
        addShopifyCartBreadcrumb("Updating buyer identity", {
          cartId,
          hasEmail: !!email
        })

        try {
          const checkoutId = await updateBuyerIdentity(client, cartId, email)
          if (checkoutId) {
            setCheckoutId(checkoutId)
            setShopifyCartContext(checkoutId, { email })
            addShopifyCartBreadcrumb("Buyer identity updated", {
              cartId: checkoutId
            })
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              context: "shopify_cart",
              operation: "context_update_buyer_identity"
            },
            extra: {
              cartId,
              email
            }
          })
          throw error
        }
      }
    }),
    [
      checkoutId,
      setCheckoutId,
      loading,
      checkout,
      config,
      client,
      ttclid,
      sproutTxid
    ]
  )

  // Initialize checkout once on mount
  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        if (config.disableEcomm) {
          return
        }
        if (!localStorage.getItem(CHECKOUT_KEY)) {
          await value.ensureCheckoutIsCreated()
        }
      } catch (error) {
        console.error("Failed to initialize checkout:", error)
      }
    }
    initializeCheckout()
  }, [config.disableEcomm, value])

  return (
    <ShopifyCartContext.Provider value={value}>
      {children}
    </ShopifyCartContext.Provider>
  )
}

const verifyShopifyCheckout = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string
): Promise<boolean> => {
  try {
    addShopifyCartBreadcrumb("Verifying checkout", { cartId })

    const { data } = await client.query({
      query: ShopifyCartDocument,
      variables: {
        cartId
      }
    })

    if (data.cart?.__typename != "Cart") {
      addShopifyCartBreadcrumb(
        "Checkout verification failed - invalid cart type",
        { cartId },
        "warning"
      )
      return false
    }

    addShopifyCartBreadcrumb("Checkout verified successfully", { cartId })
    return true
  } catch (error) {
    addShopifyCartBreadcrumb(
      "Checkout verification error",
      { cartId, error },
      "error"
    )
    // Error already captured by Apollo error link
    return false
  }
}

export const createShopifyCheckout = async (
  client: ApolloClient<NormalizedCacheObject>,
  publicConfig: PublicConfigValues,
  input: CartInput = {},
  ttclid?: string | null,
  sproutTxid?: string | null
) => {
  addShopifyCartBreadcrumb("Creating Shopify checkout", {
    hasTtclid: !!ttclid,
    hasSproutTxid: !!sproutTxid
  })

  const customAttributes = await getCustomAttributes(
    publicConfig.googleAnalyticsMeasurementId
  )

  const filteredAttributes = input.attributes?.filter(i => i.value !== "") ?? []

  const redirectAttributes = getRedirectAttributes(publicConfig)

  const analyticsAttributes = []

  if (ttclid) {
    analyticsAttributes.push({
      key: "ttclid",
      value: ttclid
    })
  }

  if (sproutTxid) {
    analyticsAttributes.push({
      key: "sprout_txid",
      value: sproutTxid
    })
  }

  const cartVariables = {
    input: {
      ...input,
      attributes: [
        ...customAttributes,
        ...filteredAttributes,
        ...redirectAttributes,
        ...analyticsAttributes,
        {
          key: "customer_app",
          value: "true"
        }
      ]
    }
  }

  uploadTracing(cartVariables, publicConfig)
    .then()
    .catch(() => {})

  const { data } = await client.mutate({
    mutation: ShopifyCartCreateDocument,
    variables: cartVariables
  })

  const cart = data?.cartCreate?.cart
  if (cart) {
    addShopifyCartBreadcrumb("Checkout created successfully", {
      cartId: cart.id
    })
    return cart
  }

  addShopifyCartBreadcrumb(
    "Failed to create checkout - no cart returned",
    {},
    "error"
  )
  const error = new Error("Shopify: Failed to create checkout.")
  Sentry.captureException(error, {
    tags: {
      context: "shopify_cart",
      operation: "create_checkout"
    },
    extra: {
      input,
      ttclid,
      sproutTxid,
      response: data
    }
  })
  throw error
}

const addLineItems = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  lineItemInputs: CartLineInput[]
): Promise<WithShopifyCartLineFragment[]> => {
  const { data } = await client.mutate({
    mutation: ShopifyCartLinesAddDocument,
    variables: {
      cartId: cartId,
      lines: lineItemInputs
    }
  })

  const cart = data?.cartLinesAdd?.cart
  if (!cart) {
    addShopifyCartBreadcrumb(
      "Cart not found when adding line items",
      { cartId },
      "error"
    )
    const error = new Error("Shopify: Cart not found.")
    Sentry.captureException(error, {
      tags: {
        context: "shopify_cart",
        operation: "add_line_items"
      },
      extra: {
        cartId,
        lineItemInputs
      }
    })
    throw error
  }

  lineItemInputs.forEach(li => {
    const cartItem = cart.lines.nodes.find(li2 =>
      li2.__typename === "CartLine"
        ? li2?.merchandise?.id === li.merchandiseId
        : false
    )
    if (!cartItem) {
      addShopifyCartBreadcrumb(
        "Line item not found in cart after adding",
        {
          cartId,
          merchandiseId: li.merchandiseId
        },
        "error"
      )
      const error = new Error("Shopify: Couldn't add line item.")
      Sentry.captureException(error, {
        tags: {
          context: "shopify_cart",
          operation: "add_line_items"
        },
        extra: {
          cartId,
          lineItemInputs,
          merchandiseId: li.merchandiseId
        }
      })
      throw error
    }
  })

  const filteredLineItems: WithShopifyCartLineFragment[] = cart.lines.nodes
    .map(li => (li.__typename != "CartLine" ? null : li))
    .filter(Boolean) as WithShopifyCartLineFragment[]

  return filteredLineItems
}

export const updateShippingAddress = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  shippingAddress: MailingAddressInput = {}
) => {
  addShopifyCartBreadcrumb("Updating shipping address", { cartId })

  try {
    const { data } = await client.mutate({
      mutation: ShopifyBuyerIdentityUpdateDocument,
      variables: {
        buyerIdentity: {
          countryCode: CountryCode.Us,
          deliveryAddressPreferences: [
            {
              deliveryAddress: { ...shippingAddress }
            }
          ]
        },
        cartId: cartId
      }
    })
    const cart = data?.cartBuyerIdentityUpdate?.cart
    if (cart) {
      addShopifyCartBreadcrumb("Shipping address updated successfully", {
        cartId
      })
      return cart
    }
  } catch (e) {
    console.log(e)
    addShopifyCartBreadcrumb(
      "Failed to update shipping address",
      { cartId, error: e },
      "error"
    )
    // Error already captured by Apollo error link
  }

  const error = new Error("Shopify: Failed to update shipping address.")
  Sentry.captureException(error, {
    tags: {
      context: "shopify_cart",
      operation: "update_shipping_address"
    },
    extra: {
      cartId,
      shippingAddress
    }
  })
  throw error
}

export const updateBuyerIdentity = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  email: string | undefined
) => {
  try {
    const { data } = await client.mutate({
      mutation: ShopifyBuyerIdentityUpdateDocument,
      variables: {
        cartId,
        buyerIdentity: {
          email
        }
      }
    })
    const checkoutId = data?.cartBuyerIdentityUpdate?.cart?.id
    if (checkoutId) return checkoutId
  } catch (e) {
    console.log(e)
    addShopifyCartBreadcrumb(
      "Failed to update buyer identity (helper)",
      { cartId, error: e },
      "error"
    )
    // Error already captured by Apollo error link
  }

  const error = new Error("Shopify: Failed to update buyer identity.")
  Sentry.captureException(error, {
    tags: {
      context: "shopify_cart",
      operation: "update_buyer_identity"
    },
    extra: {
      cartId,
      email
    }
  })
  throw error
}

export const updateDiscountCode = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  discountCode: string
) => {
  addShopifyCartBreadcrumb("Updating discount code", { cartId, discountCode })

  try {
    const { data } = await client.mutate({
      mutation: ShopifyCartDiscountCodesUpdateDocument,
      variables: {
        cartId,
        discountCodes: [discountCode]
      }
    })

    const cart = data?.cartDiscountCodesUpdate?.cart
    if (cart) {
      addShopifyCartBreadcrumb("Discount code updated successfully", {
        cartId,
        discountCode
      })
      return cart
    }
  } catch (e) {
    console.log(e)
    addShopifyCartBreadcrumb(
      "Failed to update discount code",
      { cartId, discountCode, error: e },
      "error"
    )
    // Error already captured by Apollo error link
  }

  const error = new Error("Shopify: Failed to update discount code.")
  Sentry.captureException(error, {
    tags: {
      context: "shopify_cart",
      operation: "update_discount_code"
    },
    extra: {
      cartId,
      discountCode
    }
  })
  throw error
}

export const updateCustomAttributes = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  attributes: AttributeInput[]
) => {
  addShopifyCartBreadcrumb("Updating custom attributes", {
    cartId,
    attributeCount: attributes.length
  })

  try {
    const { data } = await client.mutate({
      mutation: ShopifyCartAttributesUpdateDocument,
      variables: {
        cartId,
        attributes
      }
    })

    const cart = data?.cartAttributesUpdate?.cart
    if (cart) {
      addShopifyCartBreadcrumb("Custom attributes updated successfully", {
        cartId
      })
      return cart
    }
  } catch (e) {
    console.log(e)
    addShopifyCartBreadcrumb(
      "Failed to update custom attributes",
      { cartId, error: e },
      "error"
    )
    // Error already captured by Apollo error link
  }

  const error = new Error("Shopify: Failed to update custom attributes.")
  Sentry.captureException(error, {
    tags: {
      context: "shopify_cart",
      operation: "update_custom_attributes"
    },
    extra: {
      cartId,
      attributes
    }
  })
  throw error
}

const addLineItem = async (
  client: ApolloClient<NormalizedCacheObject>,
  cartId: string,
  merchandiseId: string,
  quantity: number,
  attributes?: AttributeInput[],
  sellingPlanId?: string
): Promise<WithShopifyCartLineFragment> => {
  const { data } = await client.mutate({
    mutation: ShopifyCartLinesAddDocument,
    variables: {
      cartId,
      lines: [
        {
          merchandiseId,
          quantity,
          attributes,
          sellingPlanId
        }
      ]
    }
  })

  const cart = data?.cartLinesAdd?.cart
  if (!cart) {
    addShopifyCartBreadcrumb(
      "Cart not found when adding line item",
      {
        cartId,
        merchandiseId
      },
      "error"
    )
    const error = new Error("Shopify: Cart not found.")
    Sentry.captureException(error, {
      tags: {
        context: "shopify_cart",
        operation: "add_line_item"
      },
      extra: {
        cartId,
        merchandiseId,
        quantity,
        attributes,
        sellingPlanId
      }
    })
    throw error
  }

  const filteredCartLines: WithShopifyCartLineFragment[] =
    cart.lines.nodes.filter(
      li => li?.__typename === "CartLine"
    ) as WithShopifyCartLineFragment[]

  const line = findLast(
    filteredCartLines,
    l => l.merchandise.id === merchandiseId
  )
  if (!line) {
    addShopifyCartBreadcrumb(
      "Line item not found in cart after adding",
      {
        cartId,
        merchandiseId,
        quantity
      },
      "error"
    )
    const error = new Error("Shopify: Couldn't add line.")
    Sentry.captureException(error, {
      tags: {
        context: "shopify_cart",
        operation: "add_line_item"
      },
      extra: {
        cartId,
        merchandiseId,
        quantity,
        attributes,
        sellingPlanId
      }
    })
    throw error
  }

  return line
}

export const useShopifyCart = () => {
  const value = useContext(ShopifyCartContext)

  if (!value) throw new Error("ShopifyCartPovider is missing.")

  return value
}

// Simple no-op provider for Storybook
export const StorybookShopifyCartProvider: FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const value = useMemo(
    () => ({
      loading: false,
      checkout: undefined,
      setCheckoutId: () => {},
      ensureCheckoutIsCreated: () => Promise.resolve("storybook-cart-id"),
      addLineItems: () => Promise.resolve([]),
      ensureCheckoutIsCreatedAndAddLineItem: () =>
        Promise.resolve({} as WithShopifyCartLineFragment),
      updateBuyerIdentity: () => {}
    }),
    []
  )

  return (
    <ShopifyCartContext.Provider value={value}>
      {children}
    </ShopifyCartContext.Provider>
  )
}

export const useShopifyQuantity = (variantCheckoutId: string | undefined) => {
  const { checkout } = useShopifyCart()
  const quantity = useMemo(
    () =>
      checkout?.lines?.nodes?.reduce((acc, e) => {
        if (e.__typename != "CartLine") return acc
        if (e.merchandise?.__typename != "ProductVariant") return acc
        const currentVariantId =
          e.merchandise.product.variants.edges[0]?.node.id
        if (
          variantCheckoutId &&
          currentVariantId ==
            toShopifyStorefrontId("ProductVariant", variantCheckoutId)
        ) {
          return e.quantity + acc
        }
        return acc
      }, 0),
    [checkout, variantCheckoutId]
  )
  return quantity ?? 0
}

const uploadTracing = async (
  cartVariables: any,
  config: PublicConfigValues
) => {
  const journeyHistory = JSON.parse(
    window.localStorage.getItem("journeyHistory") || "[]"
  )

  const tracingData = {
    journeyHistory,
    cartVariables,
    userAgent: window.navigator.userAgent
  }

  const tracingEndpoint = new URL(config.backendBase.replace("cached-", ""))
  tracingEndpoint.pathname = "/debug/trace"
  tracingEndpoint.searchParams.set("tag", "createShopifyCheckout")
  await fetch(tracingEndpoint, {
    method: "POST",
    body: JSON.stringify(tracingData),
    headers: {
      "Content-Type": "application/json"
    }
  })
}
